import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from '../lib/api'
import { useCanal, usePresencia } from '../lib/tiempoReal'
import { Campo } from '../componentes/Campo'
import { BarraFases } from '../componentes/BarraFases'
import { AccesoTemporal } from '../componentes/AccesoTemporal'
import { Cronometro } from '../componentes/Cronometro'
import { ResumenContrato } from '../componentes/ResumenContrato'
import { clasesDeColor, estaAbierta, etiquetaDeEstado, etiquetaDeSuministro } from '../lib/estados'
import { puede } from '../lib/permisos'
import type {
  Catalogos,
  Fase,
  ItemCatalogo,
  Rol,
  RespuestaFicha,
  TarifaConPuntos,
  Usuario,
} from '../lib/tipos'

// Los tipos de gestión no son un catálogo editable: son los valores del ENUM.
const TIPOS_GESTION = [
  { codigo: 'ninguna', etiqueta: 'Ninguna (solo cambio de compañía)' },
  { codigo: 'alta_nueva', etiqueta: 'Alta nueva o baja de más de 3 años' },
  { codigo: 'alta_menos_3', etiqueta: 'Alta con baja de menos de 3 años' },
  { codigo: 'alta_no_sabe', etiqueta: 'Alta, el cliente no sabe' },
  { codigo: 'cambio_titular', etiqueta: 'Cambio de titular' },
  { codigo: 'modificacion_tecnica', etiqueta: 'Modificación técnica' },
]

/** Quién puede pasarle la llamada a otro comercial. */
const REASIGNAN: Rol[] = ['jefe_equipo', 'supervisor', 'jefe_ventas', 'backoffice', 'admin']

const euros = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' })

export function Ficha({
  uuid,
  usuario,
  ir,
}: {
  uuid: string
  usuario: Usuario
  ir: (destino: string) => void
}) {
  const clienteQuery = useQueryClient()
  const [aviso, setAviso] = useState<string | null>(null)
  const [pegas, setPegas] = useState<string[]>([])
  const [final, setFinal] = useState('')
  // El texto de protección de datos se pliega al leerlo, pero no se cierra.
  const [lopdAbierta, setLopdAbierta] = useState(false)
  const [direccionManual, setDireccionManual] = useState(false)
  const [destino, setDestino] = useState('')
  // Qué se está mirando: el resumen o una fase. Con el contrato ya lanzado se
  // puede volver a las fases anteriores, que es lo que pidió José el 15 de
  // octubre; mientras la llamada está abierta manda la fase de verdad.
  const [verResumen, setVerResumen] = useState<boolean | null>(null)
  const [faseElegida, setFaseElegida] = useState<Fase | null>(null)

  const consulta = useQuery({
    queryKey: ['ficha', uuid],
    queryFn: () => api<RespuestaFicha>(`/api/fichas/${uuid}`),
  })

  const catalogos = useQuery({
    queryKey: ['catalogos'],
    queryFn: () => api<Catalogos>('/api/catalogos'),
  })

  // Las tarifas se piden aparte porque su orden depende de la provincia: salen
  // de más a menos puntos, para que el comercial empiece por la que más renta.
  const provincia = String(consulta.data?.ficha.datos.cp ?? '').slice(0, 2)

  const tarifario = useQuery({
    queryKey: ['tarifas', provincia],
    queryFn: () => api<{ tarifas: TarifaConPuntos[] }>(`/api/tarifas?provincia=${provincia}`),
  })

  const puedeReasignar = REASIGNAN.includes(usuario.rol)

  const usuarios = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api<{ usuarios: Array<{ id: number; nombre: string; rol: Rol }> }>('/api/usuarios'),
    enabled: puedeReasignar,
  })

  // El texto legal se arma con los datos de la ficha y solo hace falta al cerrar.
  const textoLegal = useQuery({
    queryKey: ['texto-legal', uuid, consulta.data?.ficha.tarifa_codigo],
    queryFn: () => api<{ texto: string; version: number }>(`/api/fichas/${uuid}/texto-legal`),
    enabled: consulta.data?.ficha.fase === 'cierre',
  })

  const canal = consulta.data?.canal
  const refrescar = () => void clienteQuery.invalidateQueries({ queryKey: ['ficha', uuid] })

  // Lo que toque otro comercial en esta misma ficha llega por aquí.
  useCanal(canal?.nombre ?? null, refrescar, canal?.token)
  const presentes = usePresencia(canal?.nombre ?? null)

  const ficha = consulta.data?.ficha
  const extra = consulta.data?.extra

  const guardar = async (campos: Record<string, unknown>) => {
    const respuesta = await api<RespuestaFicha>(`/api/fichas/${uuid}`, {
      metodo: 'PATCH',
      cuerpo: campos,
    })
    clienteQuery.setQueryData(['ficha', uuid], (previo: RespuestaFicha | undefined) => ({
      ...respuesta,
      canal: previo?.canal,
    }))
    return respuesta
  }

  const accion = useMutation({
    mutationFn: ({ ruta, cuerpo }: { ruta: string; cuerpo?: Record<string, unknown> }) =>
      api<RespuestaFicha>(`/api/fichas/${uuid}/${ruta}`, { metodo: 'POST', cuerpo: cuerpo ?? {} }),
    onSuccess: (respuesta) => {
      setAviso(null)
      setPegas([])
      clienteQuery.setQueryData(['ficha', uuid], (previo: RespuestaFicha | undefined) => ({
        ...respuesta,
        canal: previo?.canal,
      }))
    },
    onError: (e) => {
      setAviso(e instanceof ErrorApi ? e.message : 'No se ha podido hacer')
      setPegas(e instanceof ErrorApi ? e.pegas : [])
    },
  })

  if (consulta.isLoading || catalogos.isLoading) {
    return <p className="px-4 py-10 text-center text-sm text-slate-400">Cargando la ficha…</p>
  }

  if (!ficha || !catalogos.data) {
    return (
      <p className="px-4 py-10 text-center text-sm text-red-600">
        No se ha podido abrir esta ficha.
      </p>
    )
  }

  const items = (tipo: string): ItemCatalogo[] => catalogos.data.catalogos[tipo] ?? []
  const dato = (clave: string): string => String(ficha.datos[clave] ?? '')
  const tiene = (tipo: string) => ficha.consentimientos.includes(tipo)
  const abierta = estaAbierta(ficha)

  // Con el contrato cerrado, lo que interesa de entrada es el resumen entero,
  // no la fase en la que se quedó la llamada.
  const puedeVerResumen = puede(usuario, 'contratos.ver_resumen')
  const resumen = (verResumen ?? !abierta) && puedeVerResumen
  const navegaFases = !abierta && puede(usuario, 'contratos.ver_fases')
  const faseVista: Fase = (navegaFases && faseElegida) || ficha.fase
  const esMia = ficha.usuario_id === usuario.id

  const finalesDeFase = items('final').filter((item) => item.fase === null || item.fase === ficha.fase)
  const conPlacas = dato('autoconsumo') === 'si'
  const hayModificacion =
    ficha.tipo_gestion === 'modificacion_tecnica' || Boolean(ficha.modificacion_tecnica)

  // El guion de preguntas que lleva cada tipología de llamada.
  const tipologia = items('motivo').find((item) => item.codigo === ficha.motivo_codigo)
  const sondeo = Array.isArray(tipologia?.meta) ? (tipologia.meta as string[]) : []

  // Más de 15.000 kWh al año es cliente de pymes, no de un comercial de energía.
  // Eran 20.000 hasta el 15 de octubre.
  const consumo = Number(dato('consumo_anual') || 0)
  const pasaDelLimite = consumo > 15000

  // El estado del punto, que se pidió el 15 de octubre. Vive en los datos de la
  // ficha, así que sigue ahí al recargar.
  const estadoSuministro = dato('estado_suministro')

  // La que ya tiene el cliente no se le puede ofrecer.
  const companiaActual = dato('compania_actual_id')
  const companias = (catalogos.data.companias ?? []).filter(
    (compania) => String(compania.id) !== companiaActual,
  )
  // Solo se le enseñan las que puede ofrecer: «las que no cumplan las
  // condiciones no se mostrarán».
  const tarifas = (tarifario.data?.tarifas ?? []).filter(
    (tarifa) =>
      (!ficha.compania_id || tarifa.compania_id === ficha.compania_id) &&
      (!ficha.tipo_gestion || tarifa.tipo_gestion === ficha.tipo_gestion) &&
      // Con placas solo valen las tarifas con excedentes.
      (!conPlacas || tarifa.requiere_autoconsumo === 1),
  )

  // La casilla viene marcada: para vender sin mantenimiento hay que quitarla.
  const conMantenimiento = dato('sin_mantenimiento') !== 'si'

  // Los campos de la dirección se despliegan solos si ya hay algo escrito.
  const hayDireccionDetallada = ['via', 'numero', 'piso', 'localidad'].some((campo) => dato(campo))
  const verDireccion = direccionManual || hayDireccionDetallada

  return (
    <div>
      <button onClick={() => ir('/contratos')} className="mb-3 text-sm text-slate-500 hover:underline">
        ← Volver a los contratos
      </button>

      {/* Arriba y a la izquierda: el tiempo de la llamada y de quién es. */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Cronometro desde={ficha.creado_en} parado={!abierta} />
        <h1 className="font-mono text-lg font-bold">{ficha.codigo}</h1>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-medium ${clasesDeColor(ficha.estado_color)}`}
        >
          {etiquetaDeEstado(ficha)}
        </span>
        <span className="text-sm text-slate-500">{ficha.telefono_origen}</span>
        {!esMia && <span className="text-sm text-slate-400">de {ficha.comercial}</span>}
        {presentes.length > 1 && (
          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
            {presentes.map((persona) => persona.nombre).join(', ')} en la ficha
          </span>
        )}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <BarraFases
          fases={catalogos.data.fases}
          actual={faseVista}
          alElegir={
            navegaFases
              ? (fase) => {
                  setFaseElegida(fase)
                  setVerResumen(false)
                }
              : undefined
          }
        />
        {puedeVerResumen && (
          <button
            onClick={() => setVerResumen(!resumen)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
              resumen
                ? 'bg-marca-700 text-white'
                : 'border border-slate-200 text-slate-500 hover:border-marca-500 dark:border-slate-700'
            }`}
          >
            Resumen
          </button>
        )}
      </div>

      {!esMia && abierta && puedeReasignar && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span>
            Esta llamada la empezó <strong>{ficha.comercial}</strong>.
          </span>
          <button
            onClick={() => accion.mutate({ ruta: 'tomar' })}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white"
          >
            Quedármela
          </button>
        </div>
      )}

      {aviso && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="font-semibold">{aviso}</p>
          {pegas.length > 0 && (
            <ul className="mt-1 list-inside list-disc">
              {pegas.map((pega) => (
                <li key={pega}>{pega}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[1fr_300px]">
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
          {resumen ? (
            <ResumenContrato ficha={ficha} catalogos={catalogos.data} usuario={usuario} />
          ) : (
            <h2 className="text-sm font-bold">
              {catalogos.data.fases.find((fase) => fase.codigo === faseVista)?.etiqueta ??
                ficha.fase_etiqueta}
            </h2>
          )}

          {!resumen && faseVista === 'presentacion' && (
            <>
              <p className="text-sm text-slate-500">
                El teléfono ya está anotado. Si la llamada no llega a nada, ciérrala con un
                final de esta fase.
              </p>
              <Campo etiqueta="Teléfono de origen" valor={ficha.telefono_origen} guardar={async () => {}} bloqueado />
            </>
          )}

          {!resumen && faseVista === 'identificacion' && (
            <>
              {/* Leído no es cerrado: se pliega y se puede volver a abrir. */}
              <div className="rounded-xl border border-marca-200 bg-marca-50 p-4 text-sm dark:border-marca-700/40 dark:bg-marca-700/10">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold">
                    {tiene('lopd') ? '✓ Protección de datos leída' : 'Lee esto antes de seguir'}
                  </p>
                  {tiene('lopd') && (
                    <button
                      onClick={() => setLopdAbierta(!lopdAbierta)}
                      className="text-xs font-medium text-marca-700 underline dark:text-marca-100"
                    >
                      {lopdAbierta ? 'Plegar' : 'Volver a abrirla'}
                    </button>
                  )}
                </div>

                {(!tiene('lopd') || lopdAbierta) && (
                  <p className="mt-2 text-slate-600 dark:text-slate-300">
                    {catalogos.data.textos.lopd?.contenido ?? 'Falta el texto de protección de datos.'}
                  </p>
                )}

                {!tiene('lopd') && (
                  <button
                    onClick={() => accion.mutate({ ruta: 'consentimiento', cuerpo: { tipo: 'lopd' } })}
                    className="mt-3 rounded-lg bg-marca-700 px-3 py-1.5 text-xs font-bold text-white"
                  >
                    El cliente está de acuerdo
                  </button>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Campo etiqueta="Nombre" valor={dato('nombre')} obligatorio enfocar guardar={(v) => guardar({ nombre: v })} />
                <Campo etiqueta="Apellidos" valor={dato('apellido1')} guardar={(v) => guardar({ apellido1: v })} />
                <Campo
                  etiqueta="Teléfono de contacto"
                  tipo="tel"
                  obligatorio
                  valor={ficha.telefono_contacto ?? ''}
                  ayuda="Por defecto el de origen; a veces llaman desde otro."
                  guardar={(v) => guardar({ telefono_contacto: v })}
                />
                <Campo
                  etiqueta="DNI, NIE o CIF"
                  valor={dato('documento_ultimos') ? `····${dato('documento_ultimos')}` : ''}
                  ayuda="Sin él no se puede leer la tarifa, pero aquí todavía es opcional."
                  guardar={(v) => guardar({ documento: v })}
                />
              </div>
            </>
          )}

          {!resumen && faseVista === 'sondeo' && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Campo etiqueta="Sector" valor={ficha.sector ?? ''} obligatorio opciones={items('sector')} guardar={(v) => guardar({ sector: v })} />
                <Campo
                  etiqueta="Tipología de la llamada"
                  valor={ficha.motivo_codigo ?? ''}
                  opciones={items('motivo')}
                  ayuda="Por qué ha llamado el cliente. De aquí salen las preguntas del sondeo."
                  guardar={(v) => guardar({ motivo_codigo: v })}
                />
              </div>

              {sondeo.length > 0 && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                  <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                    Sondeo · pregúntale esto
                  </h3>
                  <ul className="space-y-1 text-sm text-slate-700 dark:text-slate-200">
                    {sondeo.map((pregunta) => (
                      <li key={pregunta}>· {pregunta}</li>
                    ))}
                  </ul>
                </div>
              )}

              {ficha.motivo_codigo && sondeo.length === 0 && (
                <p className="text-xs text-slate-400">
                  Esta tipología todavía no tiene guion de sondeo. Se añade desde Mantenimiento.
                </p>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <Campo
                  etiqueta="Gestión de electricidad"
                  valor={ficha.tipo_gestion ?? ''}
                  obligatorio
                  opciones={TIPOS_GESTION}
                  ayuda="Una sola lista, en vez de ir preguntando sí o no."
                  guardar={(v) => guardar({ tipo_gestion: v })}
                />
                <Campo
                  etiqueta="¿Es su vivienda habitual?"
                  valor={dato('vivienda_habitual')}
                  opciones={items('si_no')}
                  ayuda="Hace falta para el bono social y para saber qué se le puede ofrecer."
                  guardar={(v) => guardar({ vivienda_habitual: v })}
                />
                <Campo
                  etiqueta="Suministros"
                  valor={dato('suministros')}
                  opciones={[
                    { codigo: 'luz', etiqueta: 'Solo luz' },
                    { codigo: 'luz_gas', etiqueta: 'Luz y gas' },
                  ]}
                  guardar={(v) => guardar({ suministros: v })}
                />
                <label className="flex items-center gap-2 self-end pb-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(ficha.modificacion_tecnica)}
                    onChange={(e) => void guardar({ modificacion_tecnica: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 accent-marca-700"
                  />
                  Lleva además modificación técnica
                </label>
              </div>
            </>
          )}

          {!resumen && faseVista === 'suministro' && (
            <>
              <Campo
                etiqueta="CUPS"
                valor={ficha.cups ?? ''}
                marcador="ES0021000000000000XX"
                valido={Boolean(ficha.cups)}
                textoValido="CUPS válido"
                ayuda={
                  ficha.cups
                    ? `Distribuidora ${extra?.distribuidora ?? ficha.cups.slice(2, 6)}`
                    : 'Se comprueban las letras de control.'
                }
                guardar={(v) => guardar({ cups: v })}
              />

              {/* Lo que José quiere ver en cuanto se valida el CUPS. Se consulta
                  por CUPS, nunca por dirección: lo de la dirección se decidió el
                  20 de octubre y está razonado en docs/CUPS-POR-DIRECCION.md.
                  Mientras el CRM esté en modo manual, esto se pregunta. */}
              <div className="grid gap-3 rounded-xl border border-slate-200 p-4 sm:grid-cols-3 dark:border-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-2 sm:col-span-3">
                  <p className="text-xs text-slate-500">
                    Potencia y consumo del punto. En cuanto esté el alta en el SIPS vienen
                    solos al validar el CUPS; mientras tanto se preguntan.
                  </p>
                  <button
                    onClick={() => accion.mutate({ ruta: 'suministro' })}
                    disabled={!ficha.cups || accion.isPending}
                    title={
                      ficha.cups
                        ? 'Vuelve a consultar el punto por su CUPS'
                        : 'Primero hace falta el CUPS: el suministro se consulta por CUPS'
                    }
                    className="shrink-0 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium transition hover:border-marca-500 disabled:opacity-50 dark:border-slate-700"
                  >
                    Traer los datos del suministro
                  </button>
                </div>
                <Campo etiqueta="Potencia P1 (kW)" valor={dato('potencia_p1')} guardar={(v) => guardar({ potencia_p1: v })} />
                <Campo etiqueta="Potencia P2 (kW)" valor={dato('potencia_p2')} guardar={(v) => guardar({ potencia_p2: v })} />
                <Campo
                  etiqueta="Consumo anual (kWh)"
                  valor={dato('consumo_anual')}
                  ayuda="Los últimos 12 meses, no el año natural."
                  guardar={(v) => guardar({ consumo_anual: v })}
                />
                {estadoSuministro !== '' && (
                  <p className="text-xs text-slate-500 sm:col-span-3">
                    Estado del suministro:{' '}
                    <strong className="font-semibold">
                      {etiquetaDeSuministro(estadoSuministro)}
                    </strong>
                    {extra?.suministro?.consultado_en && ' · consultado ahora'}
                  </p>
                )}
              </div>

              {estadoSuministro === 'suspendido' && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-bold">El suministro está suspendido por impago</p>
                  <p>
                    Solo lo admiten las tarifas que lo tengan marcado: el resto dará error al
                    elegirlas. Cuenta con que pueda caer en scoring.
                  </p>
                </div>
              )}

              {pasaDelLimite && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800">
                  <p className="font-bold">
                    Este suministro consume {consumo.toLocaleString('es-ES')} kWh al año
                  </p>
                  <p>
                    Pasa de 15.000 kWh, así que no lo puedes tramitar tú. Ya le ha entrado como
                    oportunidad al departamento de pymes y grandes cuentas:{' '}
                    <strong>no hace falta que avises a nadie</strong>.
                  </p>
                </div>
              )}

              {hayModificacion && (
                <div className="grid gap-3 rounded-xl border border-slate-200 p-4 sm:grid-cols-[1fr_1fr_auto] dark:border-slate-700">
                  <p className="text-xs text-slate-500 sm:col-span-3">
                    Modificación técnica: a qué potencia quiere pasar en cada periodo.
                  </p>
                  <Campo
                    etiqueta="Nueva potencia P1 (kW)"
                    valor={dato('nueva_potencia_p1')}
                    obligatorio
                    guardar={(v) => guardar({ nueva_potencia_p1: v })}
                  />
                  <Campo
                    etiqueta="Nueva potencia P2 (kW)"
                    valor={dato('nueva_potencia_p2')}
                    guardar={(v) => guardar({ nueva_potencia_p2: v })}
                  />

                  {ficha.coste_potencia ? (
                    <div className="rounded-lg bg-slate-50 p-3 text-xs dark:bg-slate-800">
                      <p className="mb-1 font-bold uppercase tracking-wide text-slate-400">
                        Le costará
                      </p>
                      <p className="text-xl font-bold tabular-nums">
                        {euros.format(ficha.coste_potencia.total)}
                      </p>
                      <ul className="mt-1 space-y-0.5 text-slate-500">
                        <li>Enganche {euros.format(ficha.coste_potencia.enganche)}</li>
                        <li>Acceso {euros.format(ficha.coste_potencia.acceso)}</li>
                        <li>Extensión {euros.format(ficha.coste_potencia.extension)}</li>
                        <li>IVA {euros.format(ficha.coste_potencia.iva)}</li>
                      </ul>
                      <p className="mt-1 text-slate-400">
                        {ficha.coste_potencia.caso === 'subida'
                          ? `Solo sobre los ${ficha.coste_potencia.kw.toLocaleString('es-ES')} kW que sube.`
                          : 'Bajar de potencia no devuelve derechos.'}
                      </p>
                    </div>
                  ) : (
                    <p className="self-center text-xs text-slate-400">
                      Pon la nueva potencia y sale el coste.
                    </p>
                  )}
                </div>
              )}

              {/* Dirección: el predictivo rellena, pero los campos se ven. */}
              <div className="grid gap-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Dirección del suministro
                  </h3>
                  <button
                    onClick={() => setDireccionManual(!verDireccion)}
                    className="text-xs font-medium text-marca-700 underline dark:text-marca-100"
                  >
                    {verDireccion ? 'Ocultar los campos' : 'Escribir o desplegar a mano'}
                  </button>
                </div>

                <Campo
                  etiqueta="Buscar la dirección"
                  valor={dato('direccion')}
                  marcador="Empieza a escribir la calle…"
                  ayuda="Al elegirla se rellenan los campos de abajo, que hay que repasar: la compañía tiene que recibir la vía exactamente igual."
                  guardar={(v) => guardar({ direccion: v })}
                />

                {verDireccion && (
                  <div className="grid gap-3 sm:grid-cols-4">
                    <Campo
                      etiqueta="Tipo de vía"
                      valor={dato('via')}
                      marcador="Calle, Avenida, Camino…"
                      guardar={(v) => guardar({ via: v })}
                    />
                    <Campo etiqueta="Número" valor={dato('numero')} guardar={(v) => guardar({ numero: v })} />
                    <Campo etiqueta="Piso y puerta" valor={dato('piso')} guardar={(v) => guardar({ piso: v })} />
                    <Campo
                      etiqueta="Código postal"
                      valor={dato('cp')}
                      obligatorio
                      valido={/^\d{5}$/.test(dato('cp'))}
                      textoValido="correcto"
                      ayuda={
                        extra?.municipios?.length
                          ? extra.municipios.map((m) => m.municipio).join(', ')
                          : 'De aquí salen municipio y provincia.'
                      }
                      guardar={(v) => guardar({ cp: v })}
                    />
                    <Campo
                      etiqueta="Localidad"
                      valor={dato('localidad')}
                      ayuda="Aparte del código postal, como se pidió."
                      guardar={(v) => guardar({ localidad: v })}
                    />
                    <Campo
                      etiqueta="Provincia"
                      valor={dato('provincia')}
                      opciones={(catalogos.data.provincias ?? []).map((provincia) => ({
                        codigo: provincia.codigo,
                        etiqueta: provincia.nombre,
                      }))}
                      guardar={(v) => guardar({ provincia: v })}
                    />
                  </div>
                )}

                {!verDireccion && (
                  <Campo
                    etiqueta="Código postal"
                    valor={dato('cp')}
                    obligatorio
                    valido={/^\d{5}$/.test(dato('cp'))}
                    textoValido="correcto"
                    ayuda={
                      extra?.municipios?.length
                        ? extra.municipios.map((m) => m.municipio).join(', ')
                        : 'Va aparte porque hace falta para la consulta del SIPS.'
                    }
                    guardar={(v) => guardar({ cp: v })}
                  />
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <Campo
                  etiqueta="Compañía actual"
                  valor={dato('compania_actual_id')}
                  opciones={[
                    ...(catalogos.data.companias ?? []).map((compania) => ({
                      codigo: String(compania.id),
                      etiqueta: compania.nombre,
                    })),
                    { codigo: 'otra', etiqueta: 'Otra' },
                  ]}
                  ayuda="No aparecerá luego entre las que se pueden ofrecer."
                  guardar={(v) => guardar({ compania_actual_id: v })}
                />
                <Campo
                  etiqueta="¿Tiene placas solares?"
                  valor={dato('autoconsumo')}
                  opciones={items('si_no')}
                  ayuda="Cuando esté el SIPS, esto vendrá solo."
                  guardar={(v) => guardar({ autoconsumo: v })}
                />
                <Campo
                  etiqueta="¿Tiene bono social?"
                  valor={dato('bono_social')}
                  opciones={items('si_no')}
                  guardar={(v) => guardar({ bono_social: v })}
                />
              </div>

              {extra?.contratos_cliente?.length ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="mb-1 font-semibold">
                    Este cliente ya tiene {extra.contratos_cliente.length} contratación
                    {extra.contratos_cliente.length > 1 ? 'es' : ''} con nosotros
                  </p>
                  <ul className="list-inside list-disc text-xs">
                    {extra.contratos_cliente.map((contrato) => (
                      <li key={contrato.codigo}>
                        {contrato.codigo} · {contrato.estado_etiqueta}
                        {contrato.cups && ` · ${contrato.cups}`}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {!ficha.cups && !tiene('localizar_cups') && (
                <div className="rounded-xl border border-slate-200 p-4 text-sm dark:border-slate-700">
                  <p className="mb-2 font-semibold">¿No tiene el CUPS a mano?</p>
                  <p className="mb-3 text-slate-600 dark:text-slate-300">
                    {catalogos.data.textos.localizar_cups?.contenido ?? 'Falta el texto de consentimiento.'}
                  </p>
                  <button
                    onClick={() => accion.mutate({ ruta: 'consentimiento', cuerpo: { tipo: 'localizar_cups' } })}
                    className="rounded-lg bg-marca-700 px-3 py-1.5 text-xs font-bold text-white"
                  >
                    Nos autoriza a localizarlo
                  </button>
                </div>
              )}
            </>
          )}

          {!resumen && faseVista === 'objeciones' && (
            <div className="grid gap-3">
              <Campo etiqueta="Objeción del cliente" valor={dato('objecion')} opciones={items('objecion')} guardar={(v) => guardar({ objecion: v })} />
              <Campo etiqueta="Notas" valor={dato('notas')} guardar={(v) => guardar({ notas: v })} />
            </div>
          )}

          {!resumen && faseVista === 'oferta' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo
                etiqueta="Compañía"
                valor={ficha.compania_id ? String(ficha.compania_id) : ''}
                obligatorio
                opciones={companias.map((compania) => ({
                  codigo: String(compania.id),
                  etiqueta: compania.nombre,
                }))}
                ayuda={companiaActual ? 'No sale la que ya tiene el cliente.' : undefined}
                guardar={(v) => guardar({ compania_id: v })}
              />
              {/* Antes de la tarifa, y marcada de salida. */}
              <label className="flex items-start gap-2 rounded-xl border border-marca-200 bg-marca-50 p-3 text-sm sm:col-span-2 dark:border-marca-700/40 dark:bg-marca-700/10">
                <input
                  type="checkbox"
                  checked={conMantenimiento}
                  onChange={(e) =>
                    void guardar(
                      e.target.checked
                        ? { sin_mantenimiento: 'no' }
                        : { sin_mantenimiento: 'si', sva: '' },
                    )
                  }
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-marca-700"
                />
                <span>
                  <strong>Con servicio de mantenimiento.</strong> Quítala solo si el cliente no lo
                  quiere.
                </span>
              </label>

              <Campo
                etiqueta="Tarifa"
                valor={ficha.tarifa_codigo ?? ''}
                obligatorio
                opciones={tarifas.map((tarifa) => ({
                  codigo: tarifa.codigo,
                  etiqueta: tarifa.nombre_visible,
                }))}
                ayuda="Salen de más a menos puntos: empieza por arriba."
                guardar={(v) => guardar({ tarifa_codigo: v })}
              />

              {conMantenimiento && (
                <Campo
                  etiqueta="Servicio de mantenimiento (SVA)"
                  valor={dato('sva')}
                  opciones={items('sva')}
                  ayuda="También de más a menos puntos. Cuesta dinero y hay que decírselo."
                  guardar={(v) => guardar({ sva: v })}
                />
              )}
              {ficha.tarifa_codigo && catalogos.data.precios?.[ficha.tarifa_codigo] && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm sm:col-span-2 dark:border-slate-700 dark:bg-slate-800">
                  <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">
                    Léele esto
                  </h3>
                  <p className="whitespace-pre-line text-slate-700 dark:text-slate-200">
                    {catalogos.data.precios[ficha.tarifa_codigo]?.contenido}
                  </p>
                </div>
              )}
              {conPlacas && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 sm:col-span-2">
                  Acuérdate de marcar batería virtual en Darwin al añadir el producto solar.
                </p>
              )}
            </div>
          )}

          {!resumen && faseVista === 'datos' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo etiqueta="Correo electrónico" tipo="email" valor={dato('email')} ayuda="Para activarle el área de cliente." guardar={(v) => guardar({ email: v })} />

              {/* Entero y en verde: con los puntitos no había forma de repasarlo. */}
              <Campo
                etiqueta="Número de cuenta"
                valor={ficha.iban ?? (ficha.iban_ultimos ? `····${ficha.iban_ultimos}` : '')}
                obligatorio
                valido={Boolean(ficha.iban)}
                textoValido="este IBAN es válido"
                ayuda={
                  ficha.iban
                    ? `Cuenta comprobada${extra?.banco ? ` · ${extra.banco}` : ''}`
                    : 'Se comprueba el IBAN entero.'
                }
                guardar={(v) => guardar({ iban: v })}
              />

              {conPlacas && (
                <label className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={Boolean(ficha.datos.bateria_virtual)}
                    onChange={(e) => void guardar({ bateria_virtual: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-red-300 accent-red-600"
                  />
                  <span>
                    <strong>He marcado la batería virtual</strong> al añadir el producto solar en
                    la plataforma de la compañía. Sin esto no se puede lanzar el pedido.
                  </span>
                </label>
              )}
            </div>
          )}

          {!resumen && faseVista === 'cierre' && (
            <div className="space-y-3 text-sm">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">
                  Texto legal · léelo tal cual
                </h3>
                <p className="whitespace-pre-line text-slate-700 dark:text-slate-200">
                  {textoLegal.data?.texto ?? 'Preparando el texto…'}
                </p>
              </div>

              <ul className="rounded-lg bg-slate-50 p-3 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                <li>CUPS: {ficha.cups ?? '—'}</li>
                <li>Tarifa: {ficha.tarifa_codigo ?? '—'}</li>
                <li>
                  Cuenta: {ficha.iban ?? (ficha.iban_ultimos ? `····${ficha.iban_ultimos}` : '—')}
                </li>
              </ul>

              <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                  Lead de telefonía
                </h3>
                <label className="mb-3 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(ficha.datos.lead_telefonia)}
                    onChange={(e) => void guardar({ lead_telefonia: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 accent-marca-700"
                  />
                  Le interesa que le llamen de telefonía
                </label>
                {Boolean(ficha.datos.lead_telefonia) && (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Campo etiqueta="Compañía actual" valor={dato('tel_compania')} guardar={(v) => guardar({ tel_compania: v })} />
                    <Campo etiqueta="Líneas móviles" tipo="number" valor={dato('tel_lineas')} guardar={(v) => guardar({ tel_lineas: v })} />
                    <Campo etiqueta="Precio actual" tipo="number" valor={dato('tel_precio')} guardar={(v) => guardar({ tel_precio: v })} />
                  </div>
                )}
              </div>
              <button
                onClick={() => accion.mutate({ ruta: 'enviar' })}
                disabled={!abierta}
                className="rounded-lg bg-marca-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-marca-800 disabled:opacity-40"
              >
                Lanzar el pedido
              </button>
            </div>
          )}

          {ficha.pegas.length > 0 && abierta && (
            <p className="text-xs text-slate-400">Para avanzar: {ficha.pegas.join(' · ')}</p>
          )}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
            <button
              onClick={() => accion.mutate({ ruta: 'fase', cuerpo: { direccion: 'anterior' } })}
              disabled={ficha.fase === 'presentacion' || !abierta}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium transition disabled:opacity-40 dark:border-slate-700"
            >
              Anterior
            </button>
            <button
              onClick={() => accion.mutate({ ruta: 'fase', cuerpo: { direccion: 'siguiente' } })}
              disabled={ficha.fase === 'cierre' || !abierta}
              className="rounded-lg bg-marca-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-marca-800 disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </section>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
              Cerrar la llamada
            </h3>
            <p className="mb-3 text-xs text-slate-500">Cada fase tiene sus propios finales.</p>
            <select
              value={final}
              onChange={(e) => setFinal(e.target.value)}
              disabled={!abierta}
              className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
            >
              <option value="">— elegir final —</option>
              {finalesDeFase.map((item) => (
                <option key={item.codigo} value={item.codigo}>
                  {item.etiqueta}
                </option>
              ))}
            </select>
            <button
              onClick={() => accion.mutate({ ruta: 'cerrar', cuerpo: { final } })}
              disabled={!final || !abierta}
              className="w-full rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:opacity-40"
            >
              Abandonar
            </button>
          </div>

          {puedeReasignar && abierta && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                Reasignar la llamada
              </h3>
              <p className="mb-3 text-xs text-slate-500">
                {usuario.rol === 'jefe_equipo'
                  ? 'Solo a comerciales de tu sala.'
                  : 'A cualquier comercial.'}
              </p>
              <select
                value={destino}
                onChange={(e) => setDestino(e.target.value)}
                className="mb-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-950"
              >
                <option value="">— elegir comercial —</option>
                {usuarios.data?.usuarios.map((persona) => (
                  <option key={persona.id} value={persona.id}>
                    {persona.nombre}
                  </option>
                ))}
              </select>
              <button
                onClick={() =>
                  accion.mutate({ ruta: 'reasignar', cuerpo: { usuario_id: Number(destino) } })
                }
                disabled={!destino}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium transition hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700"
              >
                Pasársela
              </button>
            </div>
          )}

          {['supervisor', 'jefe_ventas', 'admin'].includes(usuario.rol) && (
            <AccesoTemporal uuid={uuid} />
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-xs dark:border-slate-800 dark:bg-slate-900">
            <h3 className="mb-2 font-bold uppercase tracking-wide text-slate-400">Consentimientos</h3>
            <ul className="space-y-1 text-slate-600 dark:text-slate-300">
              <li>{tiene('lopd') ? '✓' : '·'} Protección de datos</li>
              <li>{tiene('localizar_cups') ? '✓' : '·'} Localizar el CUPS</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  )
}
